/**
 * `/internal/egress-links/*` contracts — the multiregion egress-exit registry
 * (#1967 R1, sub-issue #1975; ADR-014).
 *
 * An egress-exit (the CONNECT forward proxy a remote-region gateway crosses
 * regions through) self-registers on boot and then heartbeats. Platform-api is
 * the single writer of the `egress_links` table (ADR-009 §1, platform-writes
 * family); gateways will read it locally — remote regions through the DTS
 * replica — to discover cross-region candidates. Operator writes are the
 * stored `disabled` toggle and the independent `dispatch_enabled` switch.
 *
 * ── Status vs derived status ───────────────────────────────────────────────
 * The STORED `status` holds decisions only: `active` / `disabled`. "The
 * heartbeat went quiet" is an observation about the clock, so `degraded` is
 * never stored — the list read derives it, and precedence is
 * `disabled` (stored) > `degraded` (heartbeat older than
 * {@link EGRESS_LINK_DEGRADED_AFTER_MS}) > `active`. A disabled link with a
 * dead heartbeat reads `disabled`: an operator switched it off, and "also
 * quiet" adds nothing actionable on top of that.
 *
 * ── Dispatch vs status ─────────────────────────────────────────────────────
 * `dispatch_enabled` is the production-admission switch, independent of
 * `status`. Register / re-register always leave it false and never update
 * it; heartbeat never touches it. A fully deployed, heartbeating, probed
 * exit is standby until ops flips the switch. Gateway candidacy reads only
 * `true` rows; the probe inventory still includes every derived-`active`
 * registration so a standby line stays warm.
 */

import {
  type ContractValidationResult,
  invalid,
  isRecord,
  valid,
} from './validation.ts';

/**
 * A heartbeat older than this makes a link read `degraded` on the list
 * surface. The exit heartbeats every ~30s (#1967 M3), so 120s means four
 * consecutive misses — a quiet link, not one slow request.
 */
export const EGRESS_LINK_DEGRADED_AFTER_MS = 120_000;

/**
 * THE staleness predicate — the 120s constant is consulted nowhere else.
 * `nowMs` is the CALLER'S clock, and which clock that is matters: the
 * platform list route passes the database's `CURRENT_TIMESTAMP` (the same
 * clock every heartbeat write used), and a future remote-replica reader
 * passes its replica's clock, accepting that cross-region skew shifts the
 * verdict (ADR-014 §5).
 */
export function isEgressHeartbeatStale(lastHeartbeatAtMs: number, nowMs: number): boolean {
  return nowMs - lastHeartbeatAtMs > EGRESS_LINK_DEGRADED_AFTER_MS;
}

/** Free-text `note` ceiling — operator background, not a document store. */
export const EGRESS_LINK_NOTE_MAX_LENGTH = 1024;

/** Exit-reported `version` ceiling — a build tag, not a changelog. */
export const EGRESS_LINK_VERSION_MAX_LENGTH = 64;

/** Stored statuses only — `degraded` is derived at read time, never stored. */
export type EgressLinkStatus = 'active' | 'disabled';

/** What a list reader acts on. See the module header for the precedence. */
export type EgressLinkDerivedStatus = 'active' | 'degraded' | 'disabled';

/**
 * `egress_id`: caller-chosen stable id (the exit's `docker run` env). Slug
 * charset so it can appear in URLs, logs and metric labels unescaped.
 */
const EGRESS_ID_RE = /^[a-z0-9][a-z0-9._-]{0,62}$/;

/** Lowercase region slug, e.g. `cn` / `jp` / `us`. */
const EGRESS_REGION_RE = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * Default `GATEWAY_REGION` for an unlabeled node (ADR-014 §1). The
 * pre-multiregion fleet is Tokyo; absent/blank env still is, so existing
 * deployments keep their exact outbound derivation without an env change.
 */
export const DEFAULT_GATEWAY_REGION = 'jp';

/**
 * Hostname or IPv4 literal for the endpoint forms below. Deliberately narrow:
 * no userinfo, no IPv6 bracket syntax — an exit endpoint is our own
 * infrastructure and can meet a strict shape; widen when a real endpoint
 * needs it, not before.
 */
const EGRESS_ENDPOINT_HOST_RE =
  /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?)*$/i;

export function isValidEgressLinkId(value: unknown): value is string {
  return typeof value === 'string' && EGRESS_ID_RE.test(value);
}

export function isValidEgressLinkRegion(value: unknown): value is string {
  return typeof value === 'string' && EGRESS_REGION_RE.test(value);
}

/**
 * `https://host[:port]` origin (no path, no query, no userinfo) or bare
 * `host:port`. `packages/contracts` compiles with no DOM lib, so the parse is
 * manual rather than `URL` (see `upstream-endpoint-url.ts` for the precedent).
 *
 * Case-insensitive on purpose: schemes and hostnames are case-insensitive,
 * paths are banned, so the value has NO case-sensitive component —
 * `validateRegisterEgressLinkRequest` lowercases before storing so
 * `HTTPS://EXIT.example:8443`, `https://Exit.Example:8443` and
 * `https://exit.example:8443` cannot register as three distinct-looking rows.
 */
export function isValidEgressLinkEndpoint(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 255) return false;
  const lowered = value.toLowerCase();
  if (lowered.startsWith('https://')) {
    const rest = lowered.slice('https://'.length);
    const [host, ...portParts] = rest.split(':');
    if (portParts.length > 1) return false;
    if (host === undefined || !EGRESS_ENDPOINT_HOST_RE.test(host)) return false;
    return portParts.length === 0 || isValidPort(portParts[0]);
  }
  const colon = lowered.lastIndexOf(':');
  if (colon <= 0) return false;
  const host = lowered.slice(0, colon);
  return EGRESS_ENDPOINT_HOST_RE.test(host) && isValidPort(lowered.slice(colon + 1));
}

function isValidPort(value: string | undefined): boolean {
  if (value === undefined || !/^\d{1,5}$/.test(value)) return false;
  const port = Number(value);
  return port >= 1 && port <= 65535;
}

/** One `egress_links` row, as served by every route in this module. */
export interface EgressLinkRecord {
  readonly egress_id: string;
  readonly region: string;
  readonly endpoint: string;
  readonly note: string | null;
  readonly status: EgressLinkStatus;
  /**
   * Production-admission switch. Independent of `status`. Register always
   * writes false; only the ops PATCH face turns it on.
   */
  readonly dispatch_enabled: boolean;
  readonly version: string | null;
  /** First registration only — a re-register does not move it. */
  readonly registered_at: string;
  /** Refreshed by heartbeat AND by re-register. */
  readonly last_heartbeat_at: string;
  readonly updated_at: string;
}

/**
 * `POST /internal/egress-links/register` — idempotent upsert by `egress_id`.
 *
 * `note` / `version` are typed as plain optionals: OMIT them to preserve the
 * stored values. An explicit `null` on the wire is TOLERATED by the validator
 * and normalized to "omitted" (JSON serializers commonly emit null for absent
 * optionals), but the type deliberately does not invite it — if #1979's ops
 * face ever wants "null means clear", that meaning is still unclaimed.
 */
export interface RegisterEgressLinkRequest {
  readonly egress_id: string;
  readonly region: string;
  readonly endpoint: string;
  /**
   * Free-text procurement/线路 background. Reported by the exit or maintained
   * by a future ops face (#1979): a re-register that omits it PRESERVES the
   * stored value rather than clearing it, so an exit that knows nothing about
   * its procurement background cannot erase what an operator wrote. Clearing
   * is the ops face's job, not this API's.
   */
  readonly note?: string;
  /** Exit-reported build version. Same preserve-when-absent rule as `note`. */
  readonly version?: string;
}

/**
 * The validator's normalized output, as the route consumes it. `null` here
 * means "not provided → preserve the stored value" — distinct from the wire
 * request type, where absence carries that meaning.
 */
export interface ValidatedRegisterEgressLink {
  readonly egress_id: string;
  readonly region: string;
  readonly endpoint: string;
  readonly note: string | null;
  readonly version: string | null;
}

export interface RegisterEgressLinkResponse {
  readonly link: EgressLinkRecord;
}

/**
 * `POST /internal/egress-links/heartbeat`. An unknown `egress_id` is 404
 * (`EGRESS_LINK_NOT_FOUND`): the exit backs off and re-registers rather than
 * the server inventing a row it has no region/endpoint for.
 */
export interface HeartbeatEgressLinkRequest {
  readonly egress_id: string;
}

export interface HeartbeatEgressLinkResponse {
  readonly ok: true;
  readonly last_heartbeat_at: string;
}

/**
 * One list entry. Deliberately NOT `EgressLinkRecord & {...}`: the record's
 * bare `status` is renamed to `stored_status` here so a consumer reaching for
 * `.status` gets a type error instead of silently acting on the stored
 * decision while the link is degraded. Act on `derived_status`;
 * `stored_status` exists to explain WHY (operator decision vs. quiet
 * heartbeat).
 */
export interface EgressLinkListEntry {
  readonly egress_id: string;
  readonly region: string;
  readonly endpoint: string;
  readonly note: string | null;
  /** The stored decision only (`active`/`disabled`) — never act on this alone. */
  readonly stored_status: EgressLinkStatus;
  /** Production-admission switch. Independent of `derived_status`. */
  readonly dispatch_enabled: boolean;
  readonly version: string | null;
  readonly registered_at: string;
  readonly last_heartbeat_at: string;
  readonly updated_at: string;
  readonly derived_status: EgressLinkDerivedStatus;
}

/** `GET /internal/egress-links`. Unpaginated: rows are one per exit, few. */
export interface ListEgressLinksResponse {
  readonly links: readonly EgressLinkListEntry[];
}

export function validateRegisterEgressLinkRequest(
  payload: unknown,
): ContractValidationResult<ValidatedRegisterEgressLink> {
  if (!isRecord(payload)) return invalid('request body must be a JSON object');
  const { egress_id, region, endpoint, note, version } = payload;
  if (!isValidEgressLinkId(egress_id)) {
    return invalid('egress_id must be a lowercase slug (a-z 0-9 . _ -, max 63 chars)');
  }
  if (!isValidEgressLinkRegion(region)) {
    return invalid('region must be a lowercase slug like cn/jp/us');
  }
  if (!isValidEgressLinkEndpoint(endpoint)) {
    return invalid('endpoint must be an https origin (https://host[:port]) or host:port');
  }
  if (note !== undefined && note !== null && typeof note !== 'string') {
    return invalid('note must be a string when present');
  }
  if (typeof note === 'string' && note.length > EGRESS_LINK_NOTE_MAX_LENGTH) {
    return invalid(`note must be at most ${EGRESS_LINK_NOTE_MAX_LENGTH} characters`);
  }
  if (version !== undefined && version !== null && typeof version !== 'string') {
    return invalid('version must be a string when present');
  }
  if (typeof version === 'string' && version.length > EGRESS_LINK_VERSION_MAX_LENGTH) {
    return invalid(`version must be at most ${EGRESS_LINK_VERSION_MAX_LENGTH} characters`);
  }
  return valid({
    egress_id,
    region,
    // Lowercased before storage: no case-sensitive component survives
    // validation (paths are banned), and normalizing here keeps three
    // spellings of one exit from reading as three endpoints.
    endpoint: endpoint.toLowerCase(),
    note: note ?? null,
    version: version ?? null,
  });
}

export function validateHeartbeatEgressLinkRequest(
  payload: unknown,
): ContractValidationResult<HeartbeatEgressLinkRequest> {
  if (!isRecord(payload)) return invalid('request body must be a JSON object');
  if (!isValidEgressLinkId(payload.egress_id)) {
    return invalid('egress_id must be a lowercase slug (a-z 0-9 . _ -, max 63 chars)');
  }
  return valid({ egress_id: payload.egress_id });
}

/**
 * `PATCH /internal/egress-links/:egressId/status` — the ops write face
 * (#1967 R5 / #1979). Blanket platform bearer only; the scoped registry
 * token cannot disable a link. Stored status is a decision (`active` /
 * `disabled`); `degraded` stays derived at read time.
 */
export interface PatchEgressLinkStatusRequest {
  readonly status: EgressLinkStatus;
}

export interface PatchEgressLinkStatusResponse {
  readonly link: EgressLinkRecord;
}

export function validatePatchEgressLinkStatusRequest(
  payload: unknown,
): ContractValidationResult<PatchEgressLinkStatusRequest> {
  if (!isRecord(payload)) return invalid('request body must be a JSON object');
  if (payload.status !== 'active' && payload.status !== 'disabled') {
    return invalid("status must be 'active' or 'disabled'");
  }
  return valid({ status: payload.status });
}

/**
 * `PATCH /internal/egress-links/:egressId` — the dispatch switch
 * (#1967 task #11 precursor). Blanket platform bearer only; the scoped
 * registry token cannot admit a link. Independent of stored `status`.
 * Gateway candidacy observes the new value at most 60s later (the
 * links-store snapshot); this write is not request-synchronous.
 */
export interface PatchEgressLinkDispatchRequest {
  readonly dispatch_enabled: boolean;
}

export interface PatchEgressLinkDispatchResponse {
  readonly link: EgressLinkRecord;
}

export function validatePatchEgressLinkDispatchRequest(
  payload: unknown,
): ContractValidationResult<PatchEgressLinkDispatchRequest> {
  if (!isRecord(payload)) return invalid('request body must be a JSON object');
  if (typeof payload.dispatch_enabled !== 'boolean') {
    return invalid('dispatch_enabled must be a boolean');
  }
  return valid({ dispatch_enabled: payload.dispatch_enabled });
}

/**
 * The one place the precedence lives, exported so the route and any future
 * reader (ops matrix, gateway discovery) derive identically.
 */
export function deriveEgressLinkStatus(
  status: EgressLinkStatus,
  heartbeatStale: boolean,
): EgressLinkDerivedStatus {
  if (status === 'disabled') return 'disabled';
  return heartbeatStale ? 'degraded' : 'active';
}
